/**
 * Produktion erfassen (R127), Route /produktion/erfassen. Für die Druckerei B&B (Rolle produktion) und die Leitung.
 *
 * Links die freigegebenen Module (Suche nach SKU oder Name, gruppiert nach Kategorie), je Zeile Menge + „+“. Rechts
 * „Zu buchen“ mit Summe, optional Bemerkung/Auftrag, „Buchen“ bucht alles in einer Transaktion. Darunter „Meine Buchungen“
 * (30 Tage) mit Storno binnen 24 h. Keine Bestände, Preise oder Kunden. Desktop zweispaltig, Tablet einspaltig mit
 * „Zu buchen“ unten angeheftet.
 */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUKTION_MAX_MENGE, PRODUKTION_STORNO_STUNDEN, type ProduktionsArtikel, type ProduktionsBuchung, type ProduktionsBuchungErgebnis } from '@storeos/contracts';
import { produktionBuchen, produktionsArtikel, produktionsBuchungen, produktionStornieren } from '../api/produktion-erfassen';
import { datumZeit } from '../components/format';
import { ton } from '../components/ton';
import { Dialog, DialogFormular, Feld, Seitenkopf, useToast, Zustand } from '../components/ui';

interface Korbzeile { artikel: ProduktionsArtikel; menge: number }

const OHNE_KATEGORIE = 'Ohne Kategorie';
/** Kanalnamen tragen die Variante oft schon am Ende („Klappkalender – Schwarz“); dann nicht doppelt zeigen. */
const ohneVariante = (name: string, variante: string | null) => (variante && name.endsWith(` – ${variante}`) ? name.slice(0, -(variante.length + 3)) : name);
const voll = (x: { name: string; variante: string | null }) => `${ohneVariante(x.name, x.variante)}${x.variante ? ` · ${x.variante}` : ''}`;
const stueck = (n: number) => `${n.toLocaleString('de-DE')} Stück`;

function Bild({ a }: { a: ProduktionsArtikel }) {
  const [kaputt, setKaputt] = useState(false);
  if (!a.bild_url || kaputt) return <span className="pe-bild leer" aria-hidden="true">{a.name.split(/\s+/).filter((w) => /^[\p{L}\d]/u.test(w)).map((w) => w[0]).slice(0, 2).join('')}</span>;
  return <img className="pe-bild" src={a.bild_url} alt="" loading="lazy" onError={() => setKaputt(true)} />;
}

export function ProduktionErfassen() {
  const qc = useQueryClient();
  const toast = useToast();
  const artikel = useQuery({ queryKey: ['produktion-erfassen', 'artikel'], queryFn: produktionsArtikel });
  const buchungen = useQuery({ queryKey: ['produktion-erfassen', 'buchungen'], queryFn: produktionsBuchungen });
  const [suche, setSuche] = useState('');
  const [eingabe, setEingabe] = useState<Record<string, string>>({});
  const [korb, setKorb] = useState<Korbzeile[]>([]);
  const [bemerkung, setBemerkung] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [bestaetigt, setBestaetigt] = useState<ProduktionsBuchungErgebnis | null>(null);
  const [storno, setStorno] = useState<ProduktionsBuchung | null>(null);
  const sucheRef = useRef<HTMLInputElement>(null);

  const gruppen = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const treffer = (artikel.data?.artikel ?? []).filter((a) => !q || a.sku.toLowerCase().includes(q) || a.name.toLowerCase().includes(q) || (a.variante ?? '').toLowerCase().includes(q));
    const je = new Map<string, ProduktionsArtikel[]>();
    for (const a of treffer) {
      const k = a.kategorie ?? OHNE_KATEGORIE;
      je.set(k, [...(je.get(k) ?? []), a]);
    }
    return [...je];
  }, [artikel.data, suche]);
  const anzahlTreffer = gruppen.reduce((s, [, l]) => s + l.length, 0);
  const summe = korb.reduce((s, z) => s + z.menge, 0);
  const imKorb = (id: string) => korb.find((z) => z.artikel.id === id)?.menge ?? 0;

  function hinzufuegen(a: ProduktionsArtikel) {
    const roh = (eingabe[a.id] ?? '').trim();
    const n = roh === '' ? 1 : Number(roh);
    if (!Number.isInteger(n) || n < 1) { toast('Bitte eine ganze Zahl ab 1 eingeben', 'fehler'); ton('fehler'); return; }
    if (imKorb(a.id) + n > PRODUKTION_MAX_MENGE) { toast(`Höchstens ${PRODUKTION_MAX_MENGE} Stück je Artikel`, 'fehler'); ton('fehler'); return; }
    setKorb((k) => (k.some((z) => z.artikel.id === a.id) ? k.map((z) => (z.artikel.id === a.id ? { ...z, menge: z.menge + n } : z)) : [...k, { artikel: a, menge: n }]));
    setEingabe((e) => ({ ...e, [a.id]: '' }));
    setBestaetigt(null);
    ton('ok');
  }
  const mengeTaste = (a: ProduktionsArtikel) => (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); hinzufuegen(a); } };
  const setzeMenge = (id: string, roh: string) => {
    const n = Number(roh);
    setKorb((k) => k.map((z) => (z.artikel.id === id ? { ...z, menge: Number.isInteger(n) && n >= 1 ? Math.min(n, PRODUKTION_MAX_MENGE) : z.menge } : z)));
  };

  async function buchen() {
    if (!korb.length || arbeitet) return;
    setArbeitet(true);
    try {
      const r = await produktionBuchen({ positionen: korb.map((z) => ({ article_id: z.artikel.id, menge: z.menge })), bemerkung: bemerkung.trim() || null });
      setBestaetigt(r);
      setKorb([]); setBemerkung('');
      ton('ok');
      void qc.invalidateQueries({ queryKey: ['produktion-erfassen', 'buchungen'] });
      void qc.invalidateQueries({ queryKey: ['artikel'] });
    } catch (e) {
      ton('fehler'); toast((e as Error).message, 'fehler');
    } finally { setArbeitet(false); }
  }

  return (
    <section className="pe">
      <Seitenkopf titel="Produktion erfassen" unter="Gedruckte Module als Zugang buchen. Menge eintragen, mit + übernehmen, dann „Buchen“." />
      {artikel.data?.shop_hinweis && <p className="warn-box" data-testid="pe-shop-hinweis">{artikel.data.shop_hinweis}</p>}

      <div className="pe-raster">
        <div className="pe-liste">
          <form className="pe-suche" role="search" onSubmit={(e) => e.preventDefault()}>
            <input ref={sucheRef} type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="SKU oder Name suchen" aria-label="Modul suchen" data-testid="pe-suche" autoFocus />
            {suche && <button type="button" className="knopf-flach" onClick={() => { setSuche(''); sucheRef.current?.focus(); }}>Leeren</button>}
          </form>
          <Zustand laden={artikel.isLoading} fehler={artikel.error}>
            {anzahlTreffer === 0 && (
              <p className="pe-leer" data-testid="pe-keine">{artikel.data?.artikel.length ? 'Kein Modul passt zur Suche.' : 'Noch keine Module für die Produktion freigegeben. Die Leitung gibt sie unter „Artikel / Lager“ frei.'}</p>
            )}
            {gruppen.map(([kategorie, liste]) => (
              <div key={kategorie} className="pe-gruppe" data-testid="pe-gruppe">
                <h2 className="pe-kategorie">{kategorie} <small>{liste.length}</small></h2>
                <ul>
                  {liste.map((a) => (
                    <li key={a.id} className={`pe-zeile${imKorb(a.id) ? ' gewaehlt' : ''}`} data-testid="pe-zeile" data-sku={a.sku}>
                      <Bild a={a} />
                      <div className="pe-text">
                        <div className="pe-name">{ohneVariante(a.name, a.variante)}{a.variante && <span className="pe-variante"> · {a.variante}</span>}</div>
                        <div className="pe-sku mono">{a.sku}{imKorb(a.id) > 0 && <span className="pe-marke"> · {stueck(imKorb(a.id))} zu buchen</span>}</div>
                      </div>
                      <input className="pe-menge" type="number" inputMode="numeric" min={1} max={PRODUKTION_MAX_MENGE} step={1} placeholder="Menge"
                        aria-label={`Menge ${voll(a)}`} value={eingabe[a.id] ?? ''}
                        onChange={(e) => setEingabe((x) => ({ ...x, [a.id]: e.target.value }))} onKeyDown={mengeTaste(a)} data-testid={`pe-menge-${a.sku}`} />
                      <button type="button" className="pe-plus" onClick={() => hinzufuegen(a)} aria-label={`${voll(a)} übernehmen`} data-testid={`pe-plus-${a.sku}`}>+</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Zustand>
        </div>

        <div className="pe-rechts">
          <aside className="pe-korb" aria-label="Zu buchen" data-testid="pe-korb">
            <h2>Zu buchen</h2>
            {bestaetigt && (
              <div className="pe-bestaetigung" role="status" data-testid="pe-bestaetigung">
                <strong>Gebucht: {stueck(bestaetigt.buchung.summe)}</strong>
                <ul>{bestaetigt.buchung.positionen.map((p) => <li key={p.id}>{p.menge} × {voll(p)} <span className="mono">{p.sku}</span></li>)}</ul>
                {bestaetigt.buchung.bemerkung && <p className="hinweis">Bemerkung: {bestaetigt.buchung.bemerkung}</p>}
              </div>
            )}
            {korb.length === 0 ? (
              !bestaetigt && <p className="hinweis" data-testid="pe-korb-leer">Noch nichts ausgewählt.</p>
            ) : (
              <ul className="pe-korb-liste">
                {korb.map((z) => (
                  <li key={z.artikel.id} data-testid="pe-korb-zeile" data-sku={z.artikel.sku}>
                    <span className="pe-korb-name">{ohneVariante(z.artikel.name, z.artikel.variante)}{z.artikel.variante && <small> · {z.artikel.variante}</small>}<small className="mono"> {z.artikel.sku}</small></span>
                    <input type="number" inputMode="numeric" min={1} max={PRODUKTION_MAX_MENGE} value={z.menge} onChange={(e) => setzeMenge(z.artikel.id, e.target.value)} aria-label={`Menge ${voll(z.artikel)} ändern`} />
                    <button type="button" className="knopf-flach" onClick={() => setKorb((k) => k.filter((x) => x.artikel.id !== z.artikel.id))} aria-label={`${voll(z.artikel)} entfernen`}>Entfernen</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="pe-summe"><span>Summe</span><strong data-testid="pe-summe">{stueck(summe)}</strong></div>
            <Feld label="Bemerkung/Auftrag (optional)">
              <input type="text" maxLength={300} value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="z. B. Auftragsnummer" data-testid="pe-bemerkung" />
            </Feld>
            <button type="button" className="knopf gross pe-buchen" disabled={!korb.length || arbeitet} onClick={() => void buchen()} data-testid="pe-buchen">
              {arbeitet ? 'Bucht …' : korb.length ? `Buchen (${korb.length} ${korb.length === 1 ? 'Position' : 'Positionen'})` : 'Buchen'}
            </button>
          </aside>
          <section className="karte pe-buchungen">
            <header><h2>Meine Buchungen <small className="hinweis">letzte 30 Tage · Storno bis {PRODUKTION_STORNO_STUNDEN} Stunden nach dem Buchen</small></h2></header>
            <Zustand laden={buchungen.isLoading} fehler={buchungen.error}>
              {buchungen.data?.length === 0 && <p className="hinweis">Noch keine Buchungen.</p>}
              <ul className="pe-buchungsliste" data-testid="pe-buchungen">
                {buchungen.data?.map((b) => (
                  <li key={b.id} className={b.storniert_at ? 'storniert' : ''} data-testid="pe-buchung">
                    <div className="pe-buchung-kopf">
                      <span>{datumZeit(b.created_at)}{b.benutzer && <span className="hinweis"> · {b.benutzer}</span>}</span>
                      <strong>{stueck(b.summe)}</strong>
                    </div>
                    <div className="pe-buchung-pos">{b.positionen.map((p) => `${p.menge} × ${voll(p)}`).join(', ')}</div>
                    {b.bemerkung && <div className="hinweis">Bemerkung: {b.bemerkung}</div>}
                    {b.storniert_at
                      ? <div className="pe-storno-text" data-testid="pe-storniert">Storniert {datumZeit(b.storniert_at)}{b.storno_grund ? `: ${b.storno_grund}` : ''}</div>
                      : b.stornierbar && <button type="button" className="knopf-zweit klein" onClick={() => setStorno(b)} data-testid="pe-storno">Stornieren</button>}
                  </li>
                ))}
              </ul>
            </Zustand>
          </section>
        </div>
      </div>

      <Dialog offen={!!storno} titel="Buchung stornieren" onSchliessen={() => setStorno(null)}>
        {storno && <StornoFormular buchung={storno} onAbbrechen={() => setStorno(null)} onFertig={() => {
          setStorno(null); toast('Storniert'); ton('ok');
          void qc.invalidateQueries({ queryKey: ['produktion-erfassen', 'buchungen'] });
        }} />}
      </Dialog>
    </section>
  );
}

function StornoFormular({ buchung, onAbbrechen, onFertig }: { buchung: ProduktionsBuchung; onAbbrechen: () => void; onFertig: () => void }) {
  const [grund, setGrund] = useState('');
  return (
    <DialogFormular onAbsenden={async () => { await produktionStornieren(buchung.id, grund.trim()); onFertig(); }} onAbbrechen={onAbbrechen} absendenText="Stornieren" gefahr sperre={grund.trim().length < 3}>
      <p>{buchung.positionen.map((p) => `${p.menge} × ${voll(p)}`).join(', ')} werden wieder abgebucht.</p>
      <Feld label="Grund"><input type="text" value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. doppelt erfasst" autoFocus data-testid="pe-storno-grund" /></Feld>
    </DialogFormular>
  );
}
